import { useCallback, useEffect, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Plus, Loader2, Ban, Play, FileText } from 'lucide-react'

interface FranchiseRow {
  id: string
  name: string
  slug: string
  status: string
  clientCount: number
  demoCount: number
  createdAt: string
}

interface FranchiseTerms {
  leaseMonthlyCents?: number | null
  includedClients?: number | null
  perClientCents?: number | null
  revenueSplitEnabled?: boolean
  revenueSplitPct?: number | null
  dealNotes?: string | null
}

const slugify = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)

const dollars = (cents?: number | null) =>
  cents == null ? '' : (cents / 100).toString()

const cents = (dollarStr: string): number | null => {
  const n = parseFloat(dollarStr)
  return Number.isFinite(n) ? Math.round(n * 100) : null
}

export default function FranchisesPage() {
  const { toast } = useToast()
  const [rows, setRows] = useState<FranchiseRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [ownerName, setOwnerName] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [ownerPassword, setOwnerPassword] = useState('')

  // Terms dialog
  const [termsFor, setTermsFor] = useState<FranchiseRow | null>(null)
  const [termsLoading, setTermsLoading] = useState(false)
  const [lease, setLease] = useState('')
  const [included, setIncluded] = useState('')
  const [perClient, setPerClient] = useState('')
  const [splitOn, setSplitOn] = useState(false)
  const [splitPct, setSplitPct] = useState('')
  const [notes, setNotes] = useState('')

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/platform/franchises')
      setRows(res.data.franchises || [])
    } catch {
      toast({ title: 'Could not load franchises', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])

  const createFranchise = async () => {
    setBusy(true)
    try {
      await api.post('/api/platform/franchises', {
        name: name.trim(),
        slug: slug.trim(),
        ownerName: ownerName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerPassword
      })
      toast({ title: 'Franchise created', description: `${name.trim()} is live. Their admin can sign in now.` })
      setCreateOpen(false)
      setName(''); setSlug(''); setSlugTouched(false)
      setOwnerName(''); setOwnerEmail(''); setOwnerPassword('')
      load()
    } catch (err: any) {
      toast({
        title: 'Could not create franchise',
        description: err?.response?.data?.message || 'Check the fields and try again.',
        variant: 'destructive'
      })
    } finally {
      setBusy(false)
    }
  }

  const toggleSuspension = async (row: FranchiseRow) => {
    const suspending = row.status !== 'suspended'
    if (suspending && !window.confirm(
      `Suspend ${row.name}? Every client under them goes dark (logins, widgets, voice) until reactivation. Nothing is deleted.`
    )) return
    setBusy(true)
    try {
      await api.post(`/api/platform/franchises/${row.id}/${suspending ? 'suspend' : 'reactivate'}`)
      toast({ title: suspending ? 'Franchise suspended' : 'Franchise reactivated' })
      load()
    } catch (err: any) {
      toast({ title: 'Action failed', description: err?.response?.data?.message, variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  const openTerms = async (row: FranchiseRow) => {
    setTermsFor(row)
    setTermsLoading(true)
    try {
      const res = await api.get(`/api/platform/franchises/${row.id}/terms`)
      const t: FranchiseTerms = res.data.terms || {}
      setLease(dollars(t.leaseMonthlyCents))
      setIncluded(t.includedClients == null ? '' : String(t.includedClients))
      setPerClient(dollars(t.perClientCents))
      setSplitOn(!!t.revenueSplitEnabled)
      setSplitPct(t.revenueSplitPct == null ? '' : String(t.revenueSplitPct))
      setNotes(t.dealNotes || '')
    } catch {
      toast({ title: 'Could not load terms', variant: 'destructive' })
      setTermsFor(null)
    } finally {
      setTermsLoading(false)
    }
  }

  const saveTerms = async () => {
    if (!termsFor) return
    setBusy(true)
    try {
      await api.put(`/api/platform/franchises/${termsFor.id}/terms`, {
        leaseMonthlyCents: lease.trim() ? cents(lease) : null,
        includedClients: included.trim() ? parseInt(included, 10) : null,
        perClientCents: perClient.trim() ? cents(perClient) : null,
        revenueSplitEnabled: splitOn,
        revenueSplitPct: splitOn && splitPct.trim() ? parseFloat(splitPct) : null,
        dealNotes: notes.trim() || null
      })
      toast({ title: 'Terms saved', description: `Deal terms updated for ${termsFor.name}.` })
      setTermsFor(null)
    } catch (err: any) {
      toast({ title: 'Could not save terms', description: err?.response?.data?.message, variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Franchises"
        subtitle="White-label resellers and their client trees. Creating one is the only way in — there is no public signup."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New Franchise
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No franchises yet. Create the first one and sign in with its owner account to see the reseller experience.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Franchise</TableHead>
                  <TableHead>Subdomain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Clients</TableHead>
                  <TableHead className="text-right">Demos</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{row.slug}</TableCell>
                    <TableCell>
                      <Badge variant={row.status === 'suspended' ? 'destructive' : 'default'}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{row.clientCount}</TableCell>
                    <TableCell className="text-right">{row.demoCount}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(row.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openTerms(row)}>
                          <FileText className="h-4 w-4 mr-1" /> Terms
                        </Button>
                        <Button
                          variant={row.status === 'suspended' ? 'default' : 'outline'}
                          size="sm"
                          disabled={busy}
                          onClick={() => toggleSuspension(row)}
                        >
                          {row.status === 'suspended'
                            ? <><Play className="h-4 w-4 mr-1" /> Reactivate</>
                            : <><Ban className="h-4 w-4 mr-1" /> Suspend</>}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create franchise */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Franchise</DialogTitle>
            <DialogDescription>
              Creates the reseller organization and its owner login. They brand it themselves once inside.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fr-name">Company name</Label>
                <Input id="fr-name" value={name} placeholder="Marketing6"
                  onChange={e => {
                    setName(e.target.value)
                    if (!slugTouched) setSlug(slugify(e.target.value))
                  }} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fr-slug">Subdomain slug</Label>
                <Input id="fr-slug" value={slug} placeholder="marketing6" className="font-mono"
                  onChange={e => { setSlugTouched(true); setSlug(slugify(e.target.value)) }} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fr-owner">Owner name</Label>
              <Input id="fr-owner" value={ownerName} placeholder="Jane Smith" onChange={e => setOwnerName(e.target.value)} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fr-email">Owner email</Label>
                <Input id="fr-email" type="email" value={ownerEmail} placeholder="owner@agency.com" onChange={e => setOwnerEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fr-pass">Temporary password</Label>
                <Input id="fr-pass" type="text" value={ownerPassword} placeholder="min 8 characters" onChange={e => setOwnerPassword(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={createFranchise}
              disabled={busy || !name.trim() || slug.length < 3 || !ownerName.trim() || !ownerEmail.includes('@') || ownerPassword.length < 8}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Franchise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal terms */}
      <Dialog open={!!termsFor} onOpenChange={open => { if (!open) setTermsFor(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deal Terms — {termsFor?.name}</DialogTitle>
            <DialogDescription>
              Root-only. Every change is written to the audit log.
            </DialogDescription>
          </DialogHeader>
          {termsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="t-lease">Lease $/mo</Label>
                  <Input id="t-lease" value={lease} placeholder="1499" onChange={e => setLease(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t-incl">Included clients</Label>
                  <Input id="t-incl" value={included} placeholder="5" onChange={e => setIncluded(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t-per">Per extra client $</Label>
                  <Input id="t-per" value={perClient} placeholder="200" onChange={e => setPerClient(e.target.value)} />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Revenue split</p>
                  <p className="text-xs text-muted-foreground">Negotiated deals only — collected via invoice line item.</p>
                </div>
                <div className="flex items-center gap-3">
                  {splitOn && (
                    <div className="flex items-center gap-1">
                      <Input value={splitPct} placeholder="50" className="w-16 text-right" onChange={e => setSplitPct(e.target.value)} />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                  <Switch checked={splitOn} onCheckedChange={setSplitOn} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="t-notes">Deal notes</Label>
                <Textarea id="t-notes" value={notes} rows={3}
                  placeholder="What was agreed, with whom, and when."
                  onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermsFor(null)}>Cancel</Button>
            <Button onClick={saveTerms} disabled={busy || termsLoading}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Terms
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
